import { isFailedTableUpdate, rowDeltaByRun, tableUpdateStatus } from "~/utils/tableUpdates"
import { DateFormatType, formatDate, isSameDay } from "~/utils/datetime"
import { formatDuration, formatNumberWithSuffix, getDurationColor } from "~/utils/formatting"
import { cn } from "~/utils/helpers"

import { Badge } from "../reusable/Badge"
import { Card, CardHeader, CardTitle } from "../reusable/Card"
import { Skeleton } from "../reusable/Skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../reusable/Table"
import { Tooltip } from "../reusable/Tooltip"
import { RowDelta } from "~/components/status/RowDelta"
import { useDisplayCompanies } from "~/components/status/DisplayCompaniesByLifeCycleStage"
import { useCoreIngestionLogs } from "~/hooks/useCoreIngestion"
import React, { useMemo, useState } from "react"
import SearchField from "~/components/planning/SearchField"

const MAX_ROWS = 150

interface IngestionLogCardProps {
  orgId?: string
  className?: string
  shouldRefresh?: boolean
  forecastOnly?: boolean
  bare?: boolean
}

export const IngestionLogCard: React.FC<IngestionLogCardProps> = ({
  orgId,
  className,
  shouldRefresh = false,
  forecastOnly = false,
  bare = false,
}) => {
  const updateMeta = useCoreIngestionLogs(orgId, shouldRefresh)
  const companies = useDisplayCompanies()
  const [searchText, setSearchText] = useState("")

  const rowDeltaByRecord = useMemo(
    () =>
      rowDeltaByRun(
        updateMeta,
        (r) => `${r.orgId}:${r.tableName}`,
        (r) => `${r.orgId}:${r.tableName}:${r.startTime.getTime()}`,
      ),
    [updateMeta],
  )

  if (!updateMeta) {
    return <Skeleton className="h-[627px]" />
  }

  const filteredUpdateMeta = updateMeta
    .filter((u) => orgId || companies?.some((c) => c.orgId === u.orgId))
    .filter((meta) => !forecastOnly || meta.tableName.toLowerCase().includes("forecast"))
    .filter((meta) => meta.tableName.toLowerCase().includes(searchText.toLowerCase()))
  const displayUpdateMeta = filteredUpdateMeta.slice(0, MAX_ROWS)

  const Wrapper: React.ElementType = bare ? "div" : Card

  return (
    <Wrapper
      className={
        bare ? "flex flex-col overflow-x-auto" : cn("overflow-scroll max-h-[500px]", className)
      }
    >
      {!bare && (
        <CardHeader>
          <div className="flex flex-row gap-2 items-center">
            <CardTitle>{forecastOnly ? "Forecast Ingestion Log" : "Ingestion Log"}</CardTitle>
            {shouldRefresh && (
              <Tooltip className="capitalize" title="Real-time ingestion errors">
                <div className="animate-ping w-2 h-2 rounded-full bg-violet-500" />
              </Tooltip>
            )}
          </div>
        </CardHeader>
      )}

      <div className="px-4 py-3">
        <SearchField
          initialSearchText={searchText}
          onSearchTextChange={setSearchText}
          placeholder="Search for table names"
        />
      </div>

      {filteredUpdateMeta.length > MAX_ROWS && (
        <div className="px-4 pb-2 label-sm text-fg-subtle">
          Showing latest {MAX_ROWS} of {filteredUpdateMeta.length} Search to narrow.
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            {!orgId && <TableHead>Company</TableHead>}
            <TableHead>Table Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead># of rows</TableHead>
            <TableHead>Row Δ</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Start time</TableHead>
            <TableHead className="text-right">End time</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayUpdateMeta.map((meta, index) => {
            const recordKey = `${meta.orgId}:${meta.tableName}:${meta.startTime.getTime()}`
            const delta = rowDeltaByRecord.get(recordKey) ?? null
            const hasFailed = isFailedTableUpdate(meta)
            const updateStatus = tableUpdateStatus(meta)

            return (
              <TableRow
                key={meta.tableName + meta.startTime}
                className={`${!isSameDay(meta.startTime, displayUpdateMeta[index + 1]?.startTime ?? meta.startTime) ? "border-b-[4px]" : ""}`}
              >
                {!orgId && (
                  <TableCell>
                    <Badge variant="outline">{meta.orgId}</Badge>
                  </TableCell>
                )}
                <TableCell className="font-medium">
                  <Badge variant="outline">{meta.tableName}</Badge>
                </TableCell>
                <TableCell>
                  <Tooltip title={updateStatus.tooltip}>
                    <Badge size="sm" variant={updateStatus.variant}>
                      {updateStatus.label}
                    </Badge>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={meta.isCached ? updateStatus.tooltip : ""}>
                    {hasFailed ? (
                      <span className="text-secondary">—</span>
                    ) : (
                      <span>{formatNumberWithSuffix(meta.nRows, 2)}</span>
                    )}
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <RowDelta delta={meta.isCached || hasFailed ? null : delta} />
                </TableCell>
                <TableCell className={getDurationColor(meta.startTime, meta.endTime)}>
                  {meta.endTime ? formatDuration(meta.startTime, meta.endTime) : "—"}
                </TableCell>
                <TableCell>
                  {meta.startTime ? formatDate(meta.startTime, DateFormatType.WithTime) : "N/A"}
                </TableCell>
                <TableCell className="text-right">
                  <span>
                    {meta.endTime ? formatDate(meta.endTime, DateFormatType.WithTime) : "N/A"}
                  </span>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Wrapper>
  )
}
